<script>
import { getPrograms, getProgramOptions, saveApplication, getTaxRates } from '@/app/api';
import {mapActions, mapState} from 'vuex';
import { PMT, PV, IRR } from 'formulajs';
import ApplicationConfig from "../config.json";

const EARLY_BUYOUT_RATES_120 = [1, 0.933224, 0.864861, 0.789344, 0.705927, 0.613787, 0.512008, 0.399582, 0.275396, 0.138221];
const EARLY_BUYOUT_RATES_180 = [1, 0.97, 0.94, 0.89, 0.85, 0.8, 0.76, 0.72, 0.68, 0.59, 0.5, 0.43, 0.35, 0.22, 0.0388];

const PROVINCIAL_TAX_RATES = {
	"AB": 0.05,
	"BC": 0.12,
	"MB": 0.12,
	"NB": 0.15,
	"NL": 0.15,
	"NT": 0.05,
	"NS": 0.14, 
	"NU": 0.05,
	"ON": 0.13,
	"PE": 0.15,
	"QC": 0.14975,
	"SK": 0.11,
	"YT": 0.05
};

export default {
	filters: {
		formatMoney(value) {
			return new Intl.NumberFormat('en-US', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2
			}).format(Number(value || 0));
		},
		formatPercent(value) {
			return Number(value || 0).toFixed(2);
		}
	},
	props: {
		application: {
			type: Object,
			default: null
		},
		getApplication: {
			type: Function,
			default: () => {}
		},
		goToNextStep: {
			type: Function,
			default: () => {}
		},
		nextStep: {
			type: Boolean,
			default: false
		},
		setNextDisabled: {
			type: Function,
			default: () => {}
		},
		showConfirmation: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			app: null,
			programOptions: null,
			provinceTaxes: null,
			availableLeaseTerms: [120, 180],
			/** String bindings for inputs — preserves trailing zeros (e.g. 150.10); vModels hold full precision numbers */
			displayEquipment: '',
			displayPayment: '',
			vModels: {
				province: '',
				calculationMode: 'inputEquipmentValue',
				program: null,
				// input values
				equipmentValue: 1000,
				payment: null,
				bonusRate: 0,
				reserveRate: 0,
				escalationRate: 0.035,
				taxRate: 0.13,
				payoutDiscountRate: 0.1299,
				payoutOwnershipTerm: 120,
				annualRate: 0.1099,
				residual: 0,
				serviceRate: 0.15,
				serviceMinimum: 0,
				leaseTermMonths: 120,
				paymentsPerYear: 12,
				// derived values
				leaseTermYears: 10,
				periodRate: 0,
				periodPayoutDiscountRate: 0,
				salesTax: 0,
				paymentWithTax: 0,
				servicePortion: 0,
				serviceTotal: 0,
				totalPayments: 0,
				totalPaymentsWithTax: 0,
				totalLeaseCost: 0,
				implicitFinanceCharge: 0,
				tffiPayout: 0,
				aprGuess: 0.1099,
				apr: 0,
				earlyBuyoutValues: [],
				irrCashflowArray: [],
				// compatibility aliases for downstream pages
				initialPayment: 0,
				equipmentPrice: 0,
				initialPaymentWithTax: 0
			}
		};
	},
	computed: {
		...mapState({
			authUser: state => state.auth.authUser,
			optionSets: state => state.application.options
		}),
		showTffiPayoutSection() {
			const rawFlag = this.authUser?.usr_ShowDealerPayout;
			return rawFlag === true || rawFlag === 1 || rawFlag === '1' || rawFlag === 'true';
		},
		formsJson() {
			return ApplicationConfig;
		}
	},
	watch: {
		'vModels.province': function() {
			this.populateRatesFromProgram();
			this.computeLeaseValues();
		},
		'vModels.equipmentValue': 'computeLeaseValues',
		'vModels.payment': 'computeLeaseValues',
		'vModels.leaseTermMonths': 'computeLeaseValues',
		'vModels.bonusRate': 'computeLeaseValues',
		'vModels.reserveRate': 'computeLeaseValues',
		'vModels.escalationRate': 'computeLeaseValues',
		'vModels.taxRate': 'computeLeaseValues',
		'vModels.payoutDiscountRate': 'computeLeaseValues',
		'vModels.payoutOwnershipTerm': 'computeLeaseValues',
		'vModels.annualRate': 'computeLeaseValues',
		'vModels.residual': 'computeLeaseValues',
		'vModels.serviceRate': 'computeLeaseValues',
		'vModels.serviceMinimum': 'computeLeaseValues',
		"nextStep": "saveAndGo"
	},
	async mounted() {
		this.saveLoading(true);
		const id = this.$route.params.applicationId;
		if (id === 'new') {
			this.app = {};
		} else {
			this.app = this.application;
		}
		await this.getPrograms();
		await this.preloadFromApplication();
		this.provinceTaxes = await getTaxRates();
		this.populateRatesFromProgram();
		this.syncInputDisplaysFromVModels();
		this.computeLeaseValues();
		this.saveLoading(false);
	},
	methods: {
		...mapActions([
			'saveLoading',
			'showErrorAlert'
		]),
		getForm(key) {
			if (!this.formsJson) {
				return;
			}
			return this.formsJson[key];
		},
		toNumber(value, fallback = 0) {
			const n = Number(value);
			return Number.isFinite(n) ? n : fallback;
		},
		/** Parse user money text; null = empty/clear, undefined = invalid intermediate (keep prior model value) */
		parseMoneyInput(str) {
			const cleaned = String(str ?? '').replace(/,/g, '').trim();
			if (cleaned === '') {
				return null;
			}
			const n = Number(cleaned);
			if (!Number.isFinite(n)) {
				return undefined;
			}
			return n;
		},
		/** Two-decimal string for mode switch / initial sync; full precision stays in vModels */
		formatMoneyInputString(value) {
			if (value === null || value === undefined || value === '') {
				return '';
			}
			const n = Number(value);
			if (!Number.isFinite(n)) {
				return '';
			}
			const rounded = Math.round(n * 100) / 100;
			return rounded.toFixed(2);
		},
		syncInputDisplaysFromVModels() {
			this.displayEquipment = this.formatMoneyInputString(this.vModels.equipmentValue);
			this.displayPayment = this.formatMoneyInputString(this.vModels.payment);
		},
		onEquipmentDisplayInput(e) {
			const raw = e.target.value;
			this.displayEquipment = raw;
			const parsed = this.parseMoneyInput(raw);
			if (parsed !== undefined) {
				this.vModels.equipmentValue = parsed;
			}
			this.computeLeaseValues();
		},
		onPaymentDisplayInput(e) {
			const raw = e.target.value;
			this.displayPayment = raw;
			const parsed = this.parseMoneyInput(raw);
			if (parsed !== undefined) {
				this.vModels.payment = parsed;
			}
			this.computeLeaseValues();
		},
		normalizeRate(value, fallback) {
			const rate = this.toNumber(value, fallback);
			if (rate > 1) {
				return rate / 100;
			}
			return rate;
		},
		extractProgramDetails(program) {
			if (!program || typeof program !== 'object') {
				return null;
			}
			let details = (program.program && typeof program.program === 'object')
				? program.program
				: program;
			// Unwrap accidental nesting like { program: { program: {...} } }.
			while (
				details
				&& typeof details === 'object'
				&& details.program
				&& typeof details.program === 'object'
				) {
				details = details.program;
			}
			return (details && typeof details === 'object') ? { ...details } : null;
		},
		setCalculationMode(mode) {
			if (this.vModels.calculationMode === mode) {
				return;
			}
			this.computeLeaseValues();
			this.vModels.calculationMode = mode;
			if (mode === 'inputEquipmentValue') {
				this.displayEquipment = this.formatMoneyInputString(this.vModels.equipmentValue);
			} else {
				this.displayPayment = this.formatMoneyInputString(this.vModels.payment);
			}
			this.computeLeaseValues();
		},
		getEarlyBuyoutRates(leaseTermMonths) {
			if (leaseTermMonths === 180) {
				return EARLY_BUYOUT_RATES_180;
			}
			return EARLY_BUYOUT_RATES_120;
		},
		buildIrrArray(totalLeaseCost, payment, escalationRate, leaseTermYears, paymentsPerYear) {
			const values = [-1 * totalLeaseCost];
			for (let year = 1; year <= leaseTermYears; year++) {
				const escalatedPayment = payment * Math.pow(1 + escalationRate, year - 1);
				for (let month = 0; month < paymentsPerYear; month++) {
					values.push(escalatedPayment);
				}
			}
			return values;
		},
		computeLeaseValues() {
			const calculationMode = this.vModels.calculationMode;
			const leaseTermMonths = this.toNumber(this.vModels.leaseTermMonths, 180);
			const paymentsPerYear = 12; // business rule: always monthly
			const leaseTermYears = leaseTermMonths / paymentsPerYear;
			const annualRate = this.toNumber(this.vModels.annualRate, 0);
			const periodRate = annualRate / paymentsPerYear;
			const bonusRate = this.toNumber(this.vModels.bonusRate, 0);
			const reserveRate = this.toNumber(this.vModels.reserveRate, 0);
			const residual = 0; // business rule: always zero residual
			const escalationRate = this.toNumber(this.vModels.escalationRate, 0);
			const taxRate = this.toNumber(this.vModels.taxRate, 0);
			const payoutDiscountRate = this.toNumber(this.vModels.payoutDiscountRate, 0);
			const payoutOwnershipTerm = this.toNumber(this.vModels.payoutOwnershipTerm, 0);
			const periodPayoutDiscountRate = payoutDiscountRate / paymentsPerYear;
			const serviceRate = this.toNumber(this.vModels.serviceRate, 0);
			const serviceMinimum = this.toNumber(this.vModels.serviceMinimum, 0);
			const hasPaymentInput = this.vModels.payment !== '' && this.vModels.payment != null;
			const hasEquipmentInput = this.vModels.equipmentValue !== '' && this.vModels.equipmentValue != null;
			let payment = hasPaymentInput ? this.toNumber(this.vModels.payment, 0) : null;
			let equipmentValue = hasEquipmentInput ? this.toNumber(this.vModels.equipmentValue, 0) : null;
			if (calculationMode === 'inputEquipmentValue') {
				payment = equipmentValue == null
					? null
					: -1 * this.toNumber(PMT(periodRate, leaseTermMonths, equipmentValue, residual, 0), 0);
			} else {
				equipmentValue = payment == null
					? null
					: -1 * this.toNumber(PV(periodRate, leaseTermMonths, payment, residual, 0), 0);
			}
			const paymentForMath = this.toNumber(payment, 0);
			const equipmentValueForMath = this.toNumber(equipmentValue, 0);
			const salesTax = taxRate * paymentForMath;
			const paymentWithTax = paymentForMath + salesTax;
			const servicePortion = Math.max(serviceMinimum, serviceRate * paymentForMath);
			const serviceTotal = servicePortion * leaseTermMonths;
			const totalPayments = paymentsPerYear * paymentForMath * ((Math.pow(1 + escalationRate, leaseTermYears) - 1) / escalationRate);
			const totalPaymentsWithTax = totalPayments * (1 + taxRate);
			const totalLeaseCost = serviceTotal + equipmentValueForMath;
			const implicitFinanceCharge = totalPayments - totalLeaseCost;
			const tffiPayout = -1 * this.toNumber(
				PV(periodPayoutDiscountRate, payoutOwnershipTerm, paymentForMath, 0, 0),
				0
			) * (1 + bonusRate) * (1 - reserveRate);
			const aprGuess = annualRate;
			const irrCashflowArray = this.buildIrrArray(totalLeaseCost, paymentForMath, escalationRate, leaseTermYears, paymentsPerYear);
			const periodicIrr = this.toNumber(IRR(irrCashflowArray, aprGuess / paymentsPerYear), 0);
			const apr = periodicIrr * paymentsPerYear;
			const earlyBuyoutValues = this.getEarlyBuyoutRates(leaseTermMonths).map((rate, index) => ({
				year: index + 1,
				rate,
				amount: totalLeaseCost * rate
			}));
			console.log("Rate refrehsed:",salesTax);
			Object.assign(this.vModels, {
				payment,
				equipmentValue,
				leaseTermMonths,
				paymentsPerYear,
				leaseTermYears,
				periodRate,
				periodPayoutDiscountRate,
				salesTax,
				paymentWithTax,
				servicePortion,
				serviceTotal,
				totalPayments,
				totalPaymentsWithTax,
				totalLeaseCost,
				implicitFinanceCharge,
				tffiPayout,
				aprGuess,
				apr,
				irrCashflowArray,
				earlyBuyoutValues,
				residual,
				// compatibility aliases
				initialPayment: paymentForMath,
				equipmentPrice: equipmentValueForMath,
				initialPaymentWithTax: paymentWithTax
			});
			this.setNextDisabled(false);
		},
		populateRatesFromProgram() {
			const pp = this.extractProgramDetails(this.vModels.program) || {};
			const programRate0 = Array.isArray(this.programOptions?.rates)
				? this.programOptions.rates[0]
				: 0;
			this.vModels.bonusRate = this.normalizeRate(pp.usr_ContractBonusInterestRate, 0);
			this.vModels.reserveRate = this.normalizeRate(pp.usr_CashReservePercentage, 0);
			this.vModels.escalationRate = this.normalizeRate(pp.usr_EscalationRate, 0);
			this.vModels.payoutDiscountRate = this.normalizeRate(pp.usr_DealerRentalInterestRate, 0);
			this.vModels.payoutOwnershipTerm = this.toNumber(pp.usr_OwnershipTerm, 0);
			this.vModels.annualRate = this.normalizeRate(programRate0, 0);
			this.vModels.serviceRate = this.normalizeRate(pp.usr_ServiceRate, 0);
			this.vModels.serviceMinimum = this.toNumber(pp.usr_ServiceMIN, 0);
			this.vModels.paymentsPerYear = 12;
			this.vModels.residual = 0;
			const taxes = this.optionSets.taxes || {};

			if (!this.vModels.province) {
				this.vModels.province = this.application?.application?.province || (this.authUser && this.authUser.province) || 'ON';
			}                
			const province = this.vModels.province;
			console.log("application Province!!!:",province);
			console.log(province);
			const taxList = Array.isArray(this.provinceTaxes)
				? this.provinceTaxes
				: (this.provinceTaxes?.Items || []);
			console.log("taxList:", taxList);
			const provinceRecord = taxList.find(item => item.id === province);

			if (provinceRecord && Array.isArray(provinceRecord.taxes)) {
				// 1. Calculate the raw total with the floating-point error
				const rawTaxRate = provinceRecord.taxes.reduce(
					(acc, item) => acc + this.toNumber(item.tax, 0),
					0
				);

				// 2. Round it to 5 decimal places (to safely handle Quebec's 0.14975)
				this.vModels.taxRate = Number(rawTaxRate.toFixed(5));

				console.log("Calculated tax rate:", this.vModels.taxRate);
			} else if (PROVINCIAL_TAX_RATES[province] !== undefined) {
				this.vModels.taxRate = PROVINCIAL_TAX_RATES[province];
			} else {
				this.vModels.taxRate = 0.13;
			}
		},
		async preloadFromApplication() {
			let programObj = null;
			if (this.app || this.application) {
				this.app = this.application ?? this.app;
				programObj = this.app.application?.program || null;
			}
			if (!programObj && this.$route.params.applicationId && this.$route.params.applicationId !== 'new') {
				const fetched = await this.getApplication();
				if (fetched) {
					this.app = fetched;
					programObj = fetched.application ? (fetched.application.program || null) : null;
				}
			}
			if (!programObj) {
				return;
			}
			this.vModels.payment = this.toNumber(
				programObj.payment != null ? programObj.payment : (programObj.initialPayment != null ? programObj.initialPayment : programObj.periodPayment),
				this.vModels.payment
			);
			this.vModels.equipmentValue = this.toNumber(
				programObj.equipmentValue != null ? programObj.equipmentValue : (programObj.equipmentPrice != null ? programObj.equipmentPrice : programObj.invoicedAmount),
				this.vModels.equipmentValue
			);
			this.vModels.leaseTermMonths = this.toNumber(
				programObj.leaseTermMonths != null ? programObj.leaseTermMonths : (programObj.actualTerm != null ? programObj.actualTerm : programObj.amortizationTerm),
				this.vModels.leaseTermMonths
			);
			this.vModels.calculationMode = programObj.calculationMode || this.vModels.calculationMode;
			const normalizedProgramDetails = this.extractProgramDetails(programObj);
			this.vModels.program = normalizedProgramDetails
				? { program: normalizedProgramDetails }
				: null;
		},
		async getPrograms() {
			const normalizeProgramShape = (program) => {
				if (!program || typeof program !== 'object') {
					return program;
				}
				if (program.program && typeof program.program === 'object') {
					return {
						program: { ...program.program }
					};
				}
				return {
					program: { ...program }
				};
			};
			const result = await getPrograms().catch(async error => {
				this.saveLoading(false);
				let errorMsg = error.toString();
				if (error.response && error.response.data && error.response.data.errorMessage) {
					errorMsg = error.response.data.errorMessage;
				}
				if (errorMsg) {
					this.errorAlert = await this.showErrorAlert(errorMsg);
				}
				return null;
			});
			if (!result || !result.programs || !Object.keys(result.programs).length) {
				return;
			}
			Object.keys(result.programs).forEach(key => {
				const program = result.programs[key];
				const programCode = (program?.program && program.program.Code != null) ? program.program.Code : program?.Code;
				if (programCode === 2 || programCode === '2') {
					this.vModels.program = normalizeProgramShape(program);
				}
			});
			if (!this.vModels.program) {
				this.vModels.program = normalizeProgramShape(result.programs[Object.keys(result.programs)[0]]);
			}
			const selectedProgramId = this.vModels.program?.program?.ProgramID;
			if (selectedProgramId) {
				await this.getProgramOptions({ programId: selectedProgramId });
			}
		},
		async getProgramOptions(payload) {
			const result = await getProgramOptions(payload).catch(async error => {
				let errorMsg = error.toString();
				if (error.response && error.response.data && error.response.data.errorMessage) {
					errorMsg = error.response.data.errorMessage;
				}
				if (errorMsg) {
					this.errorAlert = await this.showErrorAlert(errorMsg);
				}
				return null;
			});
			if (!result) {
				return;
			}
			this.programOptions = result;
			// TODO: throw error if no defined term is found (120 or 180)
			if (Array.isArray(result.amortTerms) && result.amortTerms.length) {
				this.availableLeaseTerms = result.amortTerms
					.map(item => this.toNumber(item, 0))
					.filter(item => item === 120 || item === 180);
				if (!this.availableLeaseTerms.length) {
					this.availableLeaseTerms = [120];
				}
				if (!this.availableLeaseTerms.includes(this.vModels.leaseTermMonths)) {
					this.vModels.leaseTermMonths = this.availableLeaseTerms[0];
				}
			}
		},
		async saveAndGo() {
			if (this.nextStep) {
				this.saveLoading(true);
				let id = this.$route?.params?.applicationId ?? "new";
				if (id === 'new') {
					id = undefined;
				}
				const normalizedProgramDetails = this.extractProgramDetails(this.vModels.program);
				const {irrCashflowArray, ...vModelsWithoutIrrCashflowArray} = this.vModels;
				const programPayload = {
					...vModelsWithoutIrrCashflowArray,
					program: normalizedProgramDetails || this.vModels.program
				};
				const result = await saveApplication({
					  id: id,
						program: programPayload
				});
				if (id) {
					await this.getApplication();
				}
				this.saveLoading(false);
				this.goToNextStep({applicationId: result?.id});
			}
		}
	}
};
</script>

<template>
	<div>
		<div class="container-xl">
			<div class="row">
				<div class="col-24">
					<div class="template__page-title">
						<h1>Lease Calculator</h1>
					</div>
				</div>
			</div>
		</div>
		<form @submit.prevent="onSubmit" novalidate>
			<div class="container-xl template--vh">
				<div class="row">
					<div class="col-lg-15 order-2 order-lg-1" v-if="!showConfirmation">
						<div class="tffi-card tffi-card-main-card payment-calculator-main-card">
							<div class="payment-calculator-main-card__small-selector-area calculation-mode-selector mb-0">
								<div class="payment-calculator-main-card__small-selector-area__label mb-3">
									<p>Calculation Mode:</p>
								</div>
								<div class="payment-calculator-main-card__small-selector-area__buttons">
									<div class="row">
										<div class="col-12 col-sm-12 col-lg-12 mb-3">
											<button
												type="button"
												class="tffi-btn tffi-btn--solid theming primary-color"
												:class="{ 'payment-calculator-main-card__buttons--active theming primary-color': vModels.calculationMode === 'inputEquipmentValue' }"
												@click="setCalculationMode('inputEquipmentValue')">
												Specify Equipment Value
											</button>
										</div>
										<div class="col-12 col-sm-12 col-lg-12 mb-3">
											<button
												type="button"
												class="tffi-btn tffi-btn--solid theming primary-color"
												:class="{ 'payment-calculator-main-card__buttons--active theming primary-color': vModels.calculationMode === 'inputPayment' }"
												@click="setCalculationMode('inputPayment')">
												Specify Monthly Payment
											</button>
										</div>
									</div>
								</div>
							</div>
							<div class="form-group mb-3">
								<label class="font-weight-bold">Province</label>
								<select class="form-control" v-model="vModels.province">
									<!-- optionSets.provinces maps the province code to its display name -->
									<option v-for="(name, code) in getForm('2.1').province.options" :key="code" :value="code">
										{{ name.en || name }}
									</option>
								</select>
							</div>
							<div v-if="vModels.calculationMode === 'inputEquipmentValue'" class="payment-calculator-main-card__financed-amount d-flex align-items-center mb-3">
								<label for="equipmentValue" class="theming primary-color fw-bold">Equipment Value</label>
								<input
									id="equipmentValue"
									:value="displayEquipment"
									name="equipmentValue"
									type="text"
									inputmode="decimal"
									class="my-0 lease-calculator-input"
									autocomplete="off"
									bootstrap
									@input="onEquipmentDisplayInput"
								>
							</div>
							<div v-if="vModels.calculationMode === 'inputPayment'" class="payment-calculator-main-card__financed-amount d-flex align-items-center mb-3">
								<label for="payment2" class="theming primary-color fw-bold">Monthly Payment (before tax)</label>
								<input
									id="payment2"
									:value="displayPayment"
									name="payment2"
									type="text"
									inputmode="decimal"
									class="my-0 lease-calculator-input"
									autocomplete="off"
									bootstrap
									@input="onPaymentDisplayInput"
								>
							</div>
						</div>
						<pre class="d-none">{{ vModels }}</pre>
					</div>
					<div :class="(!showConfirmation === true) ? 'col-lg-9 col-t1 order-1 order-lg-2' : 'installment-loan-estimate-template col-sm-24 col-md-14 col-lg-10 mx-auto col-t2'">
						<div class="tffi-card tffi-card-summary-card">
							<div class="tffi-card-summary-card__top-area theming primary-color">
								<div class="tffi-card-summary-card__top-area__overlay-image"></div>
								<div class="tffi-card-summary-card__top-area__title-area">
									<h2>Lease Program</h2>
								</div>
							</div>
							<div class="tffi-card-summary-card__bottom-area">
								<div class="tffi-card-summary-card__bottom-area__label lease-preview-highlight">
									<p class="theming primary-color fw-bold">Monthly Payment (before tax):</p>
									<span class="flex-grow-1 d-none d-lg-flex"></span>
									<p class="theming primary-color fw-bold">${{ vModels.payment | formatMoney }}</p>
								</div>
								<div class="tffi-card-summary-card__bottom-area__label">
									<p>Sales Tax Rate:</p>
									<span class="flex-grow-1 d-none d-lg-flex"></span>
									<p>{{ (vModels.taxRate * 100) | formatPercent }}%</p>
								</div>
								<div class="tffi-card-summary-card__bottom-area__label">
									<p>Monthly Payment (tax included):</p>
									<span class="flex-grow-1 d-none d-lg-flex"></span>
									<p>${{ vModels.paymentWithTax | formatMoney }}</p>
								</div>
								<div class="tffi-card-summary-card__bottom-area__label">
									<p>Amortization Term:</p>
									<span class="flex-grow-1 d-none d-lg-flex"></span>
									<p>{{ vModels.leaseTermMonths }} month</p>
								</div>
								<div class="tffi-card-summary-card__bottom-area__label lease-preview-highlight">
									<p class="theming primary-color fw-bold">Equipment Value:</p>
									<span class="flex-grow-1 d-none d-lg-flex"></span>
									<p class="theming primary-color fw-bold">${{ vModels.equipmentValue | formatMoney }}</p>
								</div>
								<div class="tffi-card-summary-card__bottom-area__label">
									<p>Total Payments (tax included):</p>
									<span class="flex-grow-1 d-none d-lg-flex"></span>
									<p>${{ vModels.totalPaymentsWithTax | formatMoney }}</p>
								</div>
								<div class="tffi-card-summary-card__bottom-area__label">
									<p>Total Lease Cost:</p>
									<span class="flex-grow-1 d-none d-lg-flex"></span>
									<p>${{ vModels.totalLeaseCost | formatMoney }}</p>
								</div>
								<div class="tffi-card-summary-card__bottom-area__label">
									<p>Implicit Finance Charge:</p>
									<span class="flex-grow-1 d-none d-lg-flex"></span>
									<p>${{ vModels.implicitFinanceCharge | formatMoney }}</p>
								</div>
							</div>
						</div>
						<div v-if="!showConfirmation && showTffiPayoutSection" class="tffi-card tffi-card-summary-card mt-3">
							<div class="tffi-card-summary-card__bottom-area">
								<div class="tffi-card-summary-card__bottom-area__label">
									<p class="theming primary-color fw-bold mb-0">TFFI Payout:</p>
									<span class="flex-grow-1 d-none d-lg-flex"></span>
									<p class="theming primary-color fw-bold mb-0">${{ vModels.tffiPayout | formatMoney }}</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</form>
	</div>
</template>

<style lang="scss" scoped>
.calculation-mode-selector .payment-calculator-main-card__small-selector-area__buttons button {
	padding: 0.85rem 1.15rem;
}
.lease-calculator-input[readonly] {
	opacity: 0.75;
	background-color: #f5f7fa;
	color: #6c757d;
	cursor: not-allowed;
}
.lease-preview-highlight {
	border-left: 3px solid var(--primary-color);
	// padding-left: 0.5rem;
}
</style>
